import { QueryIntent } from './queryAnalyzerLLM';
import { analyzeContentStyle, ContentAnalysis } from './contentStyleAnalyzer';
import { designSystems, DesignTokens } from './designSystems';
import { loadGoogleFonts } from './fontLoader';

export interface AdvancedTheme extends DesignTokens {
  style: string;
  analysis: ContentAnalysis;
}

export async function generateAdvancedTheme(
  query: string, 
  intent: QueryIntent
): Promise<AdvancedTheme> {
  console.log('=== Generating Advanced Theme ===');
  console.log('Query:', query);
  
  try {
    // Analyze content style
    const analysis = await analyzeContentStyle(query, intent);
    
    // Get design tokens for the detected style
    const tokens = designSystems[analysis.style];
    console.log('Selected theme style:', analysis.style);
    console.log('Theme colors:', tokens.colors);
    
    // Load required Google Fonts
    if (tokens.typography.googleFonts.length > 0) {
      console.log('Loading fonts:', tokens.typography.googleFonts);
      await loadGoogleFonts(tokens.typography.googleFonts).catch(err => {
        console.warn('Font loading failed, continuing with fallbacks:', err);
      });
    }
    
    // Apply theme to CSS variables
    applyAdvancedTheme(tokens, analysis.style);
    
    return {
      ...tokens,
      style: analysis.style,
      analysis
    };
  } catch (error) {
    console.error('Advanced theme generation failed:', error);
    const defaultTokens = designSystems.DEFAULT;
    applyAdvancedTheme(defaultTokens, 'DEFAULT');
    
    return {
      ...defaultTokens,
      style: 'DEFAULT',
      analysis: {
        intent,
        style: 'DEFAULT',
        styleConfidence: 0,
        keywords: [],
        mood: 'muted'
      }
    };
  }
}

export function applyAdvancedTheme(tokens: DesignTokens, style: string) {
  console.log(`Applying theme: ${style}`);
  const root = document.documentElement;
  
  // Apply colors
  Object.entries(tokens.colors).forEach(([key, value]) => {
    root.style.setProperty(`--color-${key}`, value);
  });
  
  // Apply typography
  root.style.setProperty('--font-heading', tokens.typography.fontFamily.heading);
  root.style.setProperty('--font-body', tokens.typography.fontFamily.body);
  root.style.setProperty('--font-mono', tokens.typography.fontFamily.mono);
  
  // Apply font sizes
  Object.entries(tokens.typography.fontSize).forEach(([key, value]) => {
    root.style.setProperty(`--font-size-${key}`, value);
  });
  
  // Apply font weights
  Object.entries(tokens.typography.fontWeight).forEach(([key, value]) => {
    root.style.setProperty(`--font-weight-${key}`, value.toString());
  });
  
  // Apply letter spacing
  Object.entries(tokens.typography.letterSpacing).forEach(([key, value]) => {
    root.style.setProperty(`--letter-spacing-${key}`, value);
  });
  
  // Apply spacing
  Object.entries(tokens.spacing.borderRadius).forEach(([key, value]) => {
    root.style.setProperty(`--radius-${key}`, value);
  });
  
  Object.entries(tokens.spacing.gap).forEach(([key, value]) => {
    root.style.setProperty(`--gap-${key}`, value);
  });
  
  // Apply effects
  Object.entries(tokens.effects.boxShadow).forEach(([key, value]) => {
    root.style.setProperty(`--shadow-${key}`, value);
  });
  
  root.style.setProperty('--border-style', tokens.effects.borderStyle);
  root.style.setProperty('--border-width', tokens.effects.borderWidth);
  root.style.setProperty('--transition', tokens.effects.transition);
  root.style.setProperty('--hover-transform', tokens.effects.hoverTransform);
  
  // Apply pattern if exists
  if (tokens.effects.pattern) {
    root.style.setProperty('--pattern', tokens.effects.pattern);
  }
  
  // Apply theme style class to body
  document.body.className = `theme-${style.toLowerCase()}`;
  
  // Apply animations
  root.style.setProperty('--animation-entrance', tokens.animations.entrance);
  root.style.setProperty('--animation-hover', tokens.animations.hover);
  root.style.setProperty('--animation-loading', tokens.animations.loading);
}

// Create CSS animations for each theme
export function injectThemeAnimations() {
  const style = document.createElement('style');
  style.textContent = `
    /* Comic animations */
    @keyframes bounceIn {
      0% { transform: scale(0.3); opacity: 0; }
      50% { transform: scale(1.05); }
      70% { transform: scale(0.9); }
      100% { transform: scale(1); opacity: 1; }
    }
    
    @keyframes shake {
      0%, 100% { transform: translateX(0); }
      25% { transform: translateX(-5px); }
      75% { transform: translateX(5px); }
    }
    
    /* Sci-fi animations */
    @keyframes fadeInUp {
      0% { opacity: 0; transform: translateY(20px); }
      100% { opacity: 1; transform: translateY(0); }
    }
    
    @keyframes glow {
      0%, 100% { filter: brightness(1); }
      50% { filter: brightness(1.2); }
    }
    
    /* Romance animations */
    @keyframes heartBeat {
      0% { transform: scale(1); }
      14% { transform: scale(1.3); }
      28% { transform: scale(1); }
      42% { transform: scale(1.3); }
      70% { transform: scale(1); }
    }
    
    @keyframes float {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-10px); }
    }
    
    /* Horror animations */
    @keyframes flicker {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.8; }
    }
    
    @keyframes throb {
      0%, 100% { transform: scale(1); opacity: 1; }
      50% { transform: scale(1.05); opacity: 0.9; }
    }
    
    /* Mystery animations */
    @keyframes reveal {
      0% { filter: blur(5px); opacity: 0.8; }
      100% { filter: blur(0); opacity: 1; }
    }
    
    /* General animations */
    @keyframes fadeIn {
      0% { opacity: 0; }
      100% { opacity: 1; }
    }
    
    @keyframes slideInUp {
      0% { transform: translateY(10px); opacity: 0; }
      100% { transform: translateY(0); opacity: 1; }
    }
    
    @keyframes lift {
      0% { transform: translateY(0); box-shadow: var(--shadow-sm); }
      100% { transform: translateY(-2px); box-shadow: var(--shadow-md); }
    }
    
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    
    /* Entrance animations */
    .animate-entrance {
      animation: var(--animation-entrance) 0.5s ease-out;
    }
    
    /* Hover animations */
    .animate-hover:hover {
      animation: var(--animation-hover) 0.5s ease-in-out;
    }
  `;
  
  document.head.appendChild(style);
}